/**
 * ReportContext
 * Provides report viewing context (tenant, assessment, base path, investor flag)
 * to all report pages. Both company and investor layouts provide this context.
 */

import { createContext, useContext, type ReactNode } from 'react';

interface HookOptions {
  tenantIdOverride?: string;
}

interface ReportContextValue {
  tenantId: string;
  assessmentId: string;
  basePath: string;
  isInvestorView: boolean;
  /** Pre-built options for useAnalysis/useAssessment/useReports hooks.
   *  Returns { tenantIdOverride } for investor views, undefined otherwise. */
  hookOptions: HookOptions | undefined;
}

const ReportContext = createContext<ReportContextValue | undefined>(undefined);

export function useReportContext(): ReportContextValue {
  const ctx = useContext(ReportContext);
  if (!ctx) {
    throw new Error('useReportContext must be used within a ReportProvider');
  }
  return ctx;
}

/**
 * Returns the report context if available, or undefined if outside a ReportProvider.
 * Useful for components that optionally render report-specific content.
 */
export function useOptionalReportContext(): ReportContextValue | undefined {
  return useContext(ReportContext);
}

interface ReportProviderProps {
  children: ReactNode;
  value: ReportContextValue;
}

export function ReportProvider({ children, value }: ReportProviderProps): JSX.Element {
  return <ReportContext.Provider value={value}>{children}</ReportContext.Provider>;
}
